// TfL severity presentation shared by London's page and its world region.
import type { RosterStatus, StatusDetail, StatusKind } from '@tol/ui/shell';
import type { LineId } from '@tol/core/types';
import type { TflLineStatus, TflLineStatusEntry } from '../shared/tfl.ts';
import { NORTHERN_CX } from './lines.ts';
export const STATUS_MS = 120000;
function isLineStatusEntry(v: unknown): v is TflLineStatusEntry {
  return (
    typeof v === 'object' &&
    v !== null &&
    'statusSeverity' in v &&
    typeof v.statusSeverity === 'number' &&
    (!('statusSeverityDescription' in v) ||
      v.statusSeverityDescription === undefined ||
      typeof v.statusSeverityDescription === 'string') &&
    (!('reason' in v) || v.reason === undefined || typeof v.reason === 'string')
  );
}
function isLineStatus(v: unknown): v is TflLineStatus {
  return (
    typeof v === 'object' &&
    v !== null &&
    'id' in v &&
    typeof v.id === 'string' &&
    (!('lineStatuses' in v) ||
      v.lineStatuses === undefined ||
      (Array.isArray(v.lineStatuses) && v.lineStatuses.every(isLineStatusEntry)))
  );
}

export interface LineStatusSummary {
  readonly sev: number;
  readonly text: string | undefined;
  readonly reason: string;
  readonly detail: readonly StatusDetail[];
}
export type LondonStatuses = Readonly<Partial<Record<LineId, LineStatusSummary>>>;

// TfL's severities (/Line/Meta/Severity) as the line list's statuses.
function tflKind(sev: number): StatusKind {
  if (sev === 10 || sev === 18) return 'good';
  if (sev === 1 || sev === 2 || sev === 4 || sev === 16) return 'closed';
  if (sev === 3 || sev === 5 || sev === 11) return 'part';
  if (sev === 6 || sev === 7 || sev === 9 || sev === 14) return 'delays';
  return 'info';
}

export function parseLondonStatus(data: unknown): LondonStatuses {
  const status: Partial<Record<LineId, LineStatusSummary>> = {};
  if (!Array.isArray(data)) throw new TypeError('Invalid line status data');
  const list: readonly unknown[] = data;
  for (const line of list) {
    if (!isLineStatus(line)) throw new TypeError('Invalid line status');
    const statuses = line.lineStatuses ?? [];
    if (statuses.length === 0) continue;
    const worst = statuses.reduce((a, b) => (b.statusSeverity < a.statusSeverity ? b : a));
    status[line.id] = {
      sev: worst.statusSeverity,
      text: worst.statusSeverityDescription,
      reason: worst.reason ?? '',
      // The disruptions' own words (a good service has none).
      detail: statuses
        .filter((x) => (x.reason ?? '').trim() !== '')
        .toSorted((a, b) => a.statusSeverity - b.statusSeverity)
        .map((x) => ({ title: x.statusSeverityDescription ?? '', text: (x.reason ?? '').trim() })),
    };
  }
  // The Charing Cross line's status is the Northern line's.
  const northern = status['northern'];
  if (northern) status[NORTHERN_CX] = northern;

  return status;
}
export function londonStatus(status: LondonStatuses, id: string): RosterStatus | undefined {
  const st = status[id];
  if (!st) return undefined;
  return {
    text: st.text ?? '',
    bad: st.sev < 10 && st.sev !== 20 && st.sev !== 18,
    title: st.reason.trim(),
    kind: tflKind(st.sev),
    ...(st.detail.length > 0 ? { detail: st.detail } : {}),
  };
}
export function londonIdle(status: LondonStatuses, id: string): string | undefined {
  const sev = status[id]?.sev;
  if (sev === 20) return 'not running now';
  return sev !== undefined && tflKind(sev) === 'closed' ? 'closed' : undefined;
}
